import { connect } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import React, { Component, Fragment } from "react";
import * as yup from "yup";

import { adminRequests } from "@/api/(requests)/admin";
import { Button2 } from "@/ui2/atoms/Button";
import { checkPermissionByRole } from "@/features/access-control/helpers/checkPermissionByRole";
import { CustomForm } from "@/component-library/CustomForm";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { DownloadButton } from "@/ui2/molecules/DownloadButton";
import Hr from "@/components/UI/Hr";
import images from "@/utils/images";
import { isUserAdmin, isUserPCP } from "@/utils/authToken";
import { List, ListItem, ListTitleBar } from "@/components/UI/ListView";
import ListLoader from "@/components/Common/Loaders/ListLoader";
import ListViewNav from "@/components/UI/ListView/ListViewNav";
import { providerRequests } from "@/api/(requests)/provider";
import SearchBar from "@/components/UI/SearchBar";
import { setDynamicRouteConfiguration } from "@/redux/actions/navbar";
import { sharedRequests } from "@/api/(requests)/shared";
import { sortUsersByDateLastNameFirstName } from "@/utils/sortingUtil";
import { toTitleCase } from "@/utils/filters";

import { fetchPatientList } from "../providerAPI";
import { renderStatusForm } from "../ReferralView";

import { ACTIVE, ADMIN, ARCHIVED, PENDING, PROVIDER } from "./constants";
import ActivePatientListItem from "./ActivePatientListItem";
import ArchivedReferralListItem from "./ArchivedReferralListItem";
import PendingPatientListItem from "./PendingPatientListItem";
import TitleBarItems from "./TitleBarItems";

const REFERRED = "REFERRED";

class PatientsList extends Component {
  constructor(props) {
    super(props);
    let pageSize = 20;

    this.state = {
      active: false,
      activePatientsToShow: pageSize,
      isAdmin: isUserAdmin(),
      isPCP: isUserPCP(),
      pageSize: pageSize,
      patientIdSetter: null,
      patientList: props.patientList ? props.patientList : undefined,
      pendingPatientsToShow: pageSize,
      referralCode: null,
      search: "",
      showStatusPopup: false,
      status: "Active",
      statusPopUpPosition: 0,
    };
  }

  filterPatients = (item, status) => item.patient_status === status;

  componentDidMount() {
    if (!this.state.patientList) {
      (async () => {
        const searchParams = { providerId: this.props.match.params.providerId };
        const cleanResponse = this.state.isAdmin
          ? await adminRequests.fetch_patient_list({ searchParams })
          : await fetchPatientList({ context: { showLoader: false } });

        /** Filter patients having REFERRED status */
        const activePatients = cleanResponse.user.filter(
          (item) => !this.filterPatients(item, REFERRED),
        );

        /** Filter patients not having REFERRED status */
        let referredPatients = cleanResponse.user.filter((item) =>
          this.filterPatients(item, REFERRED),
        );

        /** Create List title configuration */
        this.initResendButtonAttributes(referredPatients);

        this.setState({
          activePatients,
          patientList: cleanResponse.user,
          referredPatients,
        });
      })();
    } else {
      let referredPatients = this.state.patientList.filter((item) =>
        this.filterPatients(item, REFERRED),
      );

      this.initResendButtonAttributes(referredPatients);
      this.setState({
        activePatients: this.state.patientList.filter(
          (item) => !this.filterPatients(item, REFERRED),
        ),
        referredPatients: referredPatients,
      });
    }
  }

  initResendButtonAttributes(referredPatients) {
    for (let patients of referredPatients) {
      patients.btnText = "Resend";
      patients.btnClr = "Btn--pri";
      patients.btnStatus = false;
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.patientList !== this.props.patientList) {
      this.setState(
        {
          patientList: this.props.patientList,
        },
        () => this.componentDidMount(),
      );
    }
  }

  componentWillUnmount() {
    //reset top mobileNav controls
    this.props.setDynamicRouteConfiguration({});
  }

  getBadgeVariant = (patient_status) => {
    switch (patient_status) {
      case "INACTIVE":
        return "error";

      case "REFERRED":
        return "neutral";

      default:
        return "success";
    }
  };

  resendBtnHandler = (email) => {
    let referredPatients = [...this.state.referredPatients];
    let referredPatient = referredPatients.find((patient) => patient.email === email);
    const referralCode = referredPatient.referral_code;
    const referralCodeObj = { referralCode };

    referredPatient.btnText = "Sending";
    referredPatient.btnClr = "Btn--gry";
    referredPatient.btnStatus = true;

    this.setState({
      referredPatients,
    });

    providerRequests
      .resend_referral_code({ context: { showLoader: false }, searchParams: referralCodeObj })
      .then(() => {
        referredPatient.btnText = "Sent";
        this.setState({
          referredPatients,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  archiveBtnHandler = (referralCode, patientName) => {
    const newState = {
      patientName,
      referralCode,
      showStatusPopup: true,
    };

    if (window.outerWidth < 990) {
      newState["statusPopUpPosition"] =
        document.getElementById(`archiveBtn-${referralCode}`).getBoundingClientRect().top - 250;
    } else {
      newState["statusPopUpPosition"] = 300;
    }

    this.setState(newState);
  };

  submitStatusHandler = async (formData, actions) => {
    actions.setSubmitting(false);
    try {
      let data = { ...formData };

      data["referralCode"] = this.state.referralCode;
      data["referral_status"] = "INACTIVE";
      await sharedRequests.update_referral({ data });
      const referredPatients = [...this.state.referredPatients].map((referral) => {
        if (referral["referral_code"] === this.state.referralCode) {
          referral["referral_status"] = data["referral_status"];
          referral["referral_status_reason"] = data["referral_status_reason"];
          referral["referral_status_reason_context"] = data["referral_status_reason_context"];
        }

        return referral;
      });

      this.setState({
        patientName: null,
        referralCode: null,
        referredPatients: referredPatients,
        showStatusPopup: false,
      });
    } catch (e) {
      console.log(e);
    }
  };

  unarchiveBtnHandler = async (referralCode) => {
    try {
      let data = {};

      data["referralCode"] = referralCode;
      data["referral_status"] = "ACTIVE";
      await sharedRequests.update_referral({ data });
      const referredPatients = [...this.state.referredPatients].map((referral) => {
        if (referral["referral_code"] === referralCode) {
          referral["referral_status"] = data["referral_status"];
        }

        return referral;
      });

      this.setState({
        patientName: null,
        referralCode: null,
        referredPatients: referredPatients,
        showStatusPopup: false,
      });
    } catch (e) {
      console.log(e);
    }
  };

  // A utility function to generate list items for mobile and desktop devices
  getRenderedListItem = (patient, i, isScreening) => {
    const badgeVariant = this.getBadgeVariant(patient.patient_status);

    // if patientList is passed in props of this component then `userType = ADMIN`
    // if patientList is not passed in props but rather fetched from api then `userType = PROVIDER`
    const userType = this.props.patientList ? ADMIN : PROVIDER;
    const isActive = patient.patient_status !== REFERRED ? true : false;
    const isPending = patient.patient_status === REFERRED ? true : false;
    const isArchived = patient.referral_status === "INACTIVE" ? true : false;

    if (isScreening) {
      return this.renderScreeningItem(patient);
    }

    if (isArchived) {
      return (
        <ArchivedReferralListItem
          badgeVariant={badgeVariant}
          index={i}
          patient={patient}
          setPatientDetails={this.setPatientDetails}
          unarchiveBtnHandler={this.unarchiveBtnHandler}
          userType={userType}
        />
      );
    } else if (isActive) {
      return (
        <ActivePatientListItem
          badgeVariant={badgeVariant}
          index={i}
          patient={patient}
          setPatientDetails={this.setPatientDetails}
          userType={userType}
        />
      );
    } else if (isPending) {
      return (
        <PendingPatientListItem
          archiveBtnHandler={this.archiveBtnHandler}
          badgeVariant={badgeVariant}
          index={i}
          patient={patient}
          resendBtnHandler={this.resendBtnHandler}
          setPatientDetails={this.setPatientDetails}
          userType={userType}
        />
      );
    }
  };

  renderScreeningItem = (item) => {
    const patientName = item.first_name + " " + item.last_name;
    const createDate = moment(parseFloat(item.created_date) * 1000).format("MM/DD/YYYY");

    let desktopListItem = (
      <div className="row no-gutters d-none d-lg-flex fs-14 align-items-center justify-content-between">
        <span
          className="col-2"
          data-placement="top"
          data-private
          style={{ overflow: "hidden", textOverflow: "ellipsis" }}
          title={patientName}
        >
          {patientName}
        </span>
        <span
          className="col-2"
          data-placement="top"
          style={{ overflow: "hidden", textOverflow: "ellipsis" }}
          title={item.email}
        >
          {item.email}
        </span>

        <span className="col-1">{item.mobile}</span>
        <span className="col-1">{createDate}</span>
        <span className="col-3" />
        <div className="col-1 d-flex d-flex tw:justify-center">{this.getDownloadButton(item)}</div>

        <div className="col-1 d-flex d-flex tw:justify-center">
          <div className="cursor ml-5">
            <img
              alt="view icon"
              onClick={() => this.viewScreeningData(item)}
              src={images("./common/view.png")}
            />
          </div>
        </div>
      </div>
    );

    let mobileListItem = (
      <div className="d-flex d-lg-none justify-content-between">
        <div className="d-flex fs-14 flex-column" style={{ width: "65%", wordWrap: "break-word" }}>
          <span className="fs-16 tw:font-bold" data-private>
            {toTitleCase(patientName)}
          </span>
          <span>{item.email}</span>
          <span>{item.mobile}</span>
        </div>

        <div className="d-flex fs-14 flex-column">
          {/*<div><Badge type={'black'} text={this.transformStatus(item.status)} /></div>*/}
          <div>
            <span>{createDate}</span>
          </div>
        </div>
      </div>
    );

    return (
      <Fragment>
        {mobileListItem}
        {desktopListItem}
      </Fragment>
    );
  };

  getDownloadButton = (item) =>
    Object.hasOwn(item, "report_data") && Object.hasOwn(item["report_data"], "url") ? (
      <DownloadButton className="tw:ml-5" onPress={() => this.downloadA5Report(item)} />
    ) : null;

  downloadA5Report = (item) => {
    window.open(item["report_data"]["url"]);
  };

  viewScreeningData = (item) => {
    let url = `/app/a5_screenings/${item["id"]}`;

    if (this.props.practiceId) {
      url += `&pcpPracticeId=${this.props.practiceId}`;
    }
    this.props.history.push(url);
  };

  setPatientDetails = (id, name) => {
    if (this.props.patientDetailsSetter) {
      this.props.patientDetailsSetter(id, name);
    } else {
      let url = `/app/patient/${id}/profile/v2`;

      if (this.props.practiceId) {
        url += `?practiceId=${this.props.practiceId}`;
      }
      this.props.history.push(url);
    }
  };

  //Update Search
  updateSearch = (e) => {
    this.setState({ search: e.target.value });
  };

  ListNavHandler = (status) => {
    this.setState({ status });
  };

  getFilteredPatients = (list) => {
    let filteredPatients = list.filter((patient) => {
      let patientsFN = patient.first_name.toLowerCase();
      let patientsLN = patient.last_name.toLowerCase();
      let fullName = `${patientsFN}` + " " + `${patientsLN}`;
      let patientEmail = patient.email;
      let patientMobile = patient.mobile;
      let filteredpatientsFN;
      let filteredpatientsLN;
      let filteredpatientsFullName;
      let filteredEmail;
      let filteredMobile;
      let primaryFilter = this.state.search.replace(
        new RegExp("[.\\\\+*?\\[\\^\\]$(){}=!<>|:\\-]", "g"),
        "\\$&",
      );

      filteredpatientsFullName = fullName.match(new RegExp("^" + primaryFilter.toLowerCase()));
      filteredpatientsFN = patientsFN.match(new RegExp("^" + primaryFilter.toLowerCase()));
      filteredpatientsLN = patientsLN.match(new RegExp("^" + primaryFilter.toLowerCase()));
      filteredEmail = patientEmail.match(new RegExp("^" + primaryFilter));
      filteredMobile = patientMobile.match(new RegExp(primaryFilter));

      return (
        filteredpatientsFullName ||
        filteredpatientsLN ||
        filteredpatientsFN ||
        filteredEmail ||
        filteredMobile
      );
    });

    return filteredPatients;
  };

  showMorePatients = (patientType) => {
    let newState = {};

    newState[patientType] = this.state[patientType] + this.state.pageSize;
    this.setState(newState);
  };

  getShowMoreButton = (patientType) => {
    return (
      <div className="d-flex align-items-center justify-content-end py-4">
        <Button2 onPress={() => this.showMorePatients(patientType)}>Show More</Button2>
      </div>
    );
  };

  render() {
    let filteredAllPatients;
    let filteredReferredPatients;
    let filteredArchivedReferrals;

    // Check patient chart access permission
    const canViewChart = checkPermissionByRole({
      permissionKey: "patientDiagnosis",
      profile: this.props.profile,
      role: this.props.profile?.role,
    });

    if (this.state.activePatients) {
      let filteredAllInactivePatients = this.getFilteredPatients(this.state.activePatients)
        .filter((Patient) => Patient.patient_status === "INACTIVE")
        .sort((p1, p2) => sortUsersByDateLastNameFirstName(p1, p2, "referred_date"));
      let filteredAllActivePatients = this.getFilteredPatients(this.state.activePatients)
        .filter((Patient) => Patient.patient_status !== "INACTIVE")
        .sort((p1, p2) => sortUsersByDateLastNameFirstName(p1, p2, "referred_date"));

      filteredAllPatients = [...filteredAllActivePatients, ...filteredAllInactivePatients];
    }
    if (this.state.referredPatients) {
      filteredReferredPatients = this.getFilteredPatients(this.state.referredPatients)
        .filter((referral) => referral["referral_status"] !== "INACTIVE")
        .sort((p1, p2) => sortUsersByDateLastNameFirstName(p1, p2, "created_date"));
    }

    if (this.state.referredPatients) {
      filteredArchivedReferrals = this.getFilteredPatients(this.state.referredPatients)
        .filter((referral) => referral["referral_status"] === "INACTIVE")
        .sort((p1, p2) => sortUsersByDateLastNameFirstName(p1, p2, "created_date"));
    }

    // will render active as well as pending base on `filteredPatients` and `patientsToShow` passed
    let patientListItems = (filteredPatients, patientsToShow, showMoreParam) => {
      return (
        <div>
          <Fragment>
            {filteredPatients &&
              filteredPatients.slice(0, patientsToShow).map((patient, i) => {
                let patientName = patient.first_name + " " + patient.last_name;
                const isActivePatient = patient.patient_status !== REFERRED;
                const canNavigate = isActivePatient && canViewChart;

                return (
                  <ListItem
                    clickHandler={
                      canNavigate
                        ? () => this.setPatientDetails(patient.patient_id, patientName)
                        : undefined
                    }
                    clickOnMobile={canNavigate}
                    key={patient.patient_id || patient.referral_code}
                  >
                    {this.getRenderedListItem(patient, i)}
                  </ListItem>
                );
              })}
          </Fragment>

          {filteredPatients &&
            patientsToShow <= filteredPatients.length &&
            this.getShowMoreButton(showMoreParam)}
        </div>
      );
    };

    return (
      <DashboardCard2
        Actions={
          <div className="tw:flex">
            <SearchBar onchange={this.updateSearch} value={this.state.search} />

            {this.state.isPCP && (
              <Link
                className="Btn d-none d-lg-block Btn--pri Btn-sm ml-4 fs-16 text-center text-white"
                to="/app/patient/add"
              >
                Add New
              </Link>
            )}
          </div>
        }
        classNameChildren="tw:-mt-3"
        id="patient-list"
        isFullBleed
        title="Patients"
      >
        {this.state.showStatusPopup && (
          <div className="referralStatusPopup-wpr position-fixed d-flex tw:justify-center">
            <div
              className="row position-absolute px-4 referralStatusPopup mx-lg-auto"
              style={{ top: this.state.statusPopUpPosition }}
            >
              <CustomForm
                initialValues={{
                  referral_status_reason: "",
                }}
                onSubmit={this.submitStatusHandler}
                render={(formikProps) =>
                  renderStatusForm(
                    formikProps,
                    () => {
                      this.setState({
                        patientName: null,
                        referralCode: null,
                        showStatusPopup: false,
                        statusPopUpPosition: 0,
                      });
                    },
                    this.state.patientName,
                  )
                }
                validationSchema={yup.object().shape({
                  referral_status_reason: yup.string().required("Reason is required"),
                })}
              />
            </div>
          </div>
        )}

        <ListViewNav
          ListNavHandler={this.ListNavHandler}
          status={this.state.status}
          tabs={["Active", "Pending", "Archived"]}
        />

        <div className="d-none d-lg-block mb-2">
          <Hr />
        </div>

        <ListTitleBar>
          <TitleBarItems
            source={this.props.patientList ? ADMIN : PROVIDER}
            status={this.state.status}
          />
        </ListTitleBar>

        <List>
          {this.state.patientList ? (
            this.state.status === ACTIVE &&
            this.state.activePatients &&
            patientListItems(
              filteredAllPatients,
              this.state.activePatientsToShow,
              "activePatientsToShow",
            )
          ) : (
            <ListLoader />
          )}

          {this.state.patientList ? (
            this.state.status === PENDING &&
            this.state.referredPatients &&
            patientListItems(
              filteredReferredPatients,
              this.state.pendingPatientsToShow,
              "pendingPatientsToShow",
            )
          ) : (
            <ListLoader />
          )}

          {this.state.patientList ? (
            this.state.status === ARCHIVED &&
            this.state.referredPatients &&
            patientListItems(
              filteredArchivedReferrals,
              this.state.pendingPatientsToShow,
              "pendingPatientsToShow",
            )
          ) : (
            <ListLoader />
          )}
        </List>
      </DashboardCard2>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps, { setDynamicRouteConfiguration })(PatientsList);
